import React from 'react';
import Tooltip from '@components/Atoms/Tooltip/Tooltip';
import { ScoreData } from '@components/HierarchicalTable/TSAdvancedHierarchicalTable';
import PercentageBox from '../../contexts/HighlightedScore/PercentageBox';

// Horizontal bar chart component for displaying score data
export const ScoreChart: React.FC<{
  score: number;
  maxScore: number;
  attempts: number;
  fontColor?: string;
}> = ({ score, maxScore, attempts, fontColor }) => {
  // Detect missing data: when maxScore === 0 AND attempts === 0, it means the topic wasn't in the exam
  const isMissingData = maxScore === 0 && attempts === 0;
  const correctPercentage = isMissingData
    ? null
    : maxScore > 0
    ? (score / maxScore) * 100
    : 0;
  const wrongAnswers = attempts - score;
  const wrongPercentage = maxScore > 0 ? (wrongAnswers / maxScore) * 100 : 0;
  const emptyAnswers = maxScore - attempts;
  const emptyPercentage = maxScore > 0 ? (emptyAnswers / maxScore) * 100 : 0;

  // Tooltip content with detailed information
  const tooltipContent = (
    <div style={{ padding: '8px', fontSize: '12px', lineHeight: '1.4' }}>
      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Score Details</div>
      <div style={{ marginBottom: '2px' }}>
        <span style={{ color: '#4CAF50', fontWeight: 'bold' }}>Correct:</span> {score}{' '}
        answers
      </div>
      <div style={{ marginBottom: '2px' }}>
        <span style={{ color: '#C62828', fontWeight: 'bold' }}>Wrong:</span>{' '}
        {wrongAnswers} answers
      </div>
      <div style={{ borderTop: '1px solid #ccc', paddingTop: '4px', marginTop: '4px' }}>
        <span style={{ fontWeight: 'bold' }}>Total Questions:</span> {maxScore}
      </div>
    </div>
  );

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        minWidth: 'fit-content',
        cursor: 'pointer',
      }}
    >
      {/* Percentage display */}
      <PercentageBox correctPercentage={correctPercentage} fontColor={fontColor} />
      {/* Horizontal Bar */}
      <Tooltip title={tooltipContent} placement="top" arrow>
        <div
          style={{
            width: '120px',
            height: '24px',
            backgroundColor: '#f0f0f0',
            borderRadius: '6px',
            overflow: 'hidden',
            display: 'flex',
            position: 'relative',
          }}
        >
          {/* Correct answers (green) */}
          {correctPercentage !== null && correctPercentage > 0 && (
            <div
              style={{
                width: `${correctPercentage}%`,
                height: '100%',
                backgroundColor: '#4CAF50',
                transition: 'width 0.3s ease',
              }}
            />
          )}
          {/* Wrong answers (red) */}
          {wrongPercentage > 0 && (
            <div
              style={{
                width: `${wrongPercentage}%`,
                height: '100%',
                backgroundColor: '#C62828',
                transition: 'width 0.3s ease',
              }}
            />
          )}
          {/* Empty answers (gray) */}
          {emptyPercentage > 0 && (
            <div
              style={{
                width: `${emptyPercentage}%`,
                height: '100%',
                backgroundColor: '#9E9E9E',
                transition: 'width 0.3s ease',
              }}
            />
          )}
        </div>
      </Tooltip>
    </div>
  );
};

// Custom cell component for hierarchical columns
export const HierarchicalScoreCell: React.FC<{
  value: string | number | null;
  isHorizontal: boolean;
  pathKey: string;
  data: ScoreData;
  colId: string;
  report: any;
  columnIndex?: number;
}> = ({ data, colId, report, isHorizontal, columnIndex = -1 }) => {
  // Get the raw score data from the original report
  const studentReport = report?.[`${data.id}`];

  // For nested columns like '132.203', we need to use just the last part (203) to look up the data
  const itemId = colId.split('.').pop() || colId;
  const rawScoreData = studentReport?.[itemId];

  // Detect missing data: undefined OR (max_score === 0 AND attempts === 0)
  const isMissingData =
    !rawScoreData || (rawScoreData.max_score === 0 && rawScoreData.attempts === 0);

  // Determine font color based on column index: white for column 2 (index 1), black otherwise
  const fontColor = columnIndex === 1 ? 'rgba(255, 255, 255, 1)' : undefined;

  if (!isHorizontal) {
    const correctPercentage = isMissingData
      ? null
      : rawScoreData
      ? (rawScoreData.score / rawScoreData.max_score) * 100
      : 0;
    return <PercentageBox correctPercentage={correctPercentage} fontColor={fontColor} />;
  }

  if (rawScoreData && !isMissingData) {
    return (
      <ScoreChart
        score={rawScoreData.score}
        maxScore={rawScoreData.max_score}
        attempts={rawScoreData.attempts}
        fontColor={fontColor}
      />
    );
  }

  // Show -- chart when data is missing
  return <ScoreChart score={0} maxScore={0} attempts={0} fontColor={fontColor} />;
};
